import { Button } from "@/ui/button";
import { ICONES, Icone, s, tinta } from "../visual.jsx";
import { useAcoes, useCelular, useEstado, useJanela, usePode, useTablet } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { acoesQueCabem } from "../cabecalho.js";
import { mascaraDeTelefone, presencaEmPalavras, telefoneLegivel } from "../conversa.js";
import { Bandeira, Etiqueta, MenuDeAcoes, Retrato } from "./comuns.jsx";
import { conectado, numeroCurto, numeroDoCaso, numeroLegivel } from "../whatsapp-estado.js";
import TextoEditavel from "./TextoEditavel.jsx";
import TrilhoDoProcesso from "./TrilhoDoProcesso.jsx";

/**
 * Um botão do topo, em três pesos.
 *
 * Eram quatro caixas com borda, do mesmo tamanho e da mesma força: a que ABRE a
 * petição (o trabalho do escritório), a que liga a IA, a que procura e a que
 * mostra o painel — quatro molduras disputando o olho, e nenhuma dizendo qual
 * era a principal. Agora o peso conta a hierarquia: `principal` é sólido
 * (petição), `ligado` é sólido escuro (o estado de quem já está aberto — busca e
 * painel são interruptores, e interruptor tem de dizer que está ligado), e o
 * resto é fantasma, que só ganha superfície debaixo do ponteiro.
 *
 * A pastilha é um ponto e não um número: "tem coisa esperando você no painel" é
 * o que faz alguém abrir; quantas, ele lê lá dentro.
 */
function BotaoDeAcao({ acao, comRotulo }) {
  /*
   * `hover:text-creme` explícito, e é correção de bug (27/08).
   *
   * `Button variant="outline"` do registro escreve `hover:text-accent-foreground`
   * — que é a TINTA. Pôr `bg-foreground` sem dizer a cor da LETRA deixava a regra
   * do preset ganhar no hover: o ícone escurecia até desaparecer dentro do botão
   * escuro, justamente quando o ponteiro encostava nele. É o mesmo defeito que o
   * trilho da lateral já documenta — duas declarações, e só uma estava escrita.
   */
  const peso =
    acao.peso === "principal" || acao.ligado
      ? "border-transparent bg-escuro text-sobre-escuro hover:bg-escuro/85 hover:text-sobre-escuro dark:hover:text-sobre-escuro"
      : "border-transparent bg-transparent text-tinta-media hover:bg-card hover:text-foreground";

  return (
    <Button
      variant="outline"
      onClick={acao.aoTocar}
      data-dica={acao.dica ?? acao.rotulo}
      aria-label={acao.rotulo}
      aria-pressed={acao.ligado === undefined ? undefined : acao.ligado}
      className={`relative h-padrao min-w-padrao flex-none gap-[7px] rounded-controle text-corpo font-semibold whitespace-nowrap transition-[background-color,color] duration-(--t-curta) ease-entra ${peso} ${
        comRotulo ? "px-3" : "px-0"
      }`}
    >
      <Icone de={acao.icone} tamanho={16} estilo={s("flex:none")} />
      {comRotulo && <span>{acao.rotulo}</span>}
      {acao.pastilha ? (
        <span
          aria-hidden="true"
          className="absolute top-[6px] right-[6px] size-[6px] rounded-full bg-primary ring-2 ring-muted"
        />
      ) : null}
    </Button>
  );
}

/**
 * O topo da conversa: quem é, em que etapa está e o que dá para fazer.
 *
 * Três ações, não cinco. As duas que saíram tinham dono melhor em outro lugar:
 * "Transferir" mora ao lado do titular, na ficha, onde a decisão tem contexto;
 * "Reunião" mora na seção de reuniões da coluna, que é a que MOSTRA que está gravando —
 * aqui um clique ligava o microfone sem confirmar nada. Com três, o rótulo cabe
 * no notebook de 1440 com o painel aberto, e o topo deixa de ser ícone mudo.
 */
/**
 * O interruptor de uma COLUNA — o da esquerda (lista) e o da direita (ficha).
 *
 * Um componente só para os dois, e é isso que os torna legíveis: mesmo tamanho,
 * mesmo canto, mesmo jeito de mostrar aberto/fechado. O que muda é o ÍCONE, que
 * desenha a caixa com o lado cheio — o lado diz qual coluna é, sem rótulo.
 *
 * Três defeitos morreram aqui:
 *
 *  - o da lista usava seta (`voltar`/`avancar`) ENCOSTADO no ✕/← de sair do
 *    atendimento, que é outra seta: duas setas vizinhas apontando para lados
 *    opostos, fazendo coisas diferentes;
 *  - o da ficha ficava dentro do orçamento de `acoesQueCabem`, e era o ÚLTIMO da
 *    fila — em conversa estreita ele caía no menu "mais", ou seja, sumia
 *    justamente onde a coluna mais atrapalha;
 *  - fechados, os dois se reabriam por uma alça de 14px na borda, alvo que se
 *    descobre por acaso.
 *
 * Agora são presos fora do orçamento, um em cada ponta, e o estado é `ligado`.
 */
function InterruptorDeColuna({ lado, aberta, aoAlternar, esperando = 0 }) {
  if (!aoAlternar) return null;
  const nome = lado === "esquerda" ? "a lista de atendimentos" : "a ficha do caso";
  /* O ponto só faz sentido FECHADA: aberta, o que espera está à vista. */
  const pastilha = !aberta && esperando > 0;
  return (
    <Button
      variant="outline"
      onClick={aoAlternar}
      aria-pressed={aberta}
      aria-label={pastilha ? `Mostrar ${nome} — ${esperando} esperando decisão` : `${aberta ? "Esconder" : "Mostrar"} ${nome}`}
      data-dica={pastilha ? `Mostrar ${nome} — ${esperando} esperando decisão` : `${aberta ? "Esconder" : "Mostrar"} ${nome}`}
      className={`alvo relative size-padrao flex-none rounded-controle p-0 ${
        aberta
          ? "border-foreground/20 bg-foreground/8 text-foreground hover:text-foreground"
          : "border-borda-controle bg-card text-tinta-fantasma hover:text-foreground"
      }`}
    >
      <Icone de={lado === "esquerda" ? ICONES.painelEsquerdo : ICONES.painel} tamanho={16} />
      {/* O que espera decisão na ficha, quando ela está fechada — era a pastilha
          do botão "Ficha", e sumiria junto com ele. */}
      {pastilha ? (
        <span aria-hidden="true" className="absolute top-[5px] right-[5px] size-[6px] rounded-full bg-primary ring-2 ring-card" />
      ) : null}
    </Button>
  );
}

/**
 * Quanto sobra de tela para a conversa depois do trilho, da lista e do painel.
 * É esta conta que decide quantas ações cabem no topo e o que a linha mostra.
 *
 * Mora no cabeçalho, que é o único que a lê: ela muda a cada pixel de arrasto,
 * e lida na `Conversa` redesenhava a trilha inteira junto com o topo.
 */
function useLarguraPrincipal(empurraPainel, listaNaTela) {
  const larguraJanela = useJanela((j) => j.largura);
  const celular = useCelular();
  const tablet = useTablet();
  const larguraPainel = empurraPainel ? (larguraJanela >= 1180 ? 330 : 300) : 0;
  /* A MESMA medida da coluna (`Lista.jsx`): esta conta decide o que aparece
     ao lado do nome e quantas ações cabem no topo — se as duas discordarem, o
     cabeçalho passa a caber por um cálculo e a transbordar na tela.
     Se a lista está na tela quem responde é a `Casca`, que é quem a monta: a
     conta refeita aqui já discordou dela uma vez (ver `listaNaTela`). */
  const larguraLista = listaNaTela ? (tablet ? 320 : 380) : 0;
  return Math.max(240, larguraJanela - (celular ? 0 : 74) - larguraLista - larguraPainel);
}

/**
 * Por qual número do SETOR esta conversa fala — só quando há mais de um.
 *
 * Com um número só, dizer qual é ruído: é sempre o mesmo. Com dois, quem
 * responde precisa saber por qual a mensagem sai (o cliente conhece aquele
 * número, e não o outro), e principalmente se ele está pareado: responder por um
 * número desligado é escrever para ninguém. Por isso o desligado é terra aqui,
 * com a dica dizendo o porquê; a frase inteira mora UMA vez, acima da barra de
 * escrever (`AvisoDoWhatsapp`), que é onde a pessoa age — eram duas faixas
 * dizendo o mesmo, uma embaixo do trilho e outra em cima do composer.
 */
function NumeroDoCaso({ numeros, conexaoId }) {
  if ((numeros?.length ?? 0) < 2 || conexaoId === undefined) return null;
  const n = numeroDoCaso(numeros, conexaoId);
  if (!n) return null;
  const pareado = conectado(n);
  const nome = `${n.rotulo || "número"} (${numeroLegivel(n.numero) || "sem número"})`;
  return (
    <span
      data-dica={
        pareado
          ? `Esta conversa sai por ${nome}`
          : `${nome} está ${n.estado || "desligado"}: a resposta NÃO sai por ele até alguém parear o número`
      }
      aria-label={pareado ? `Fala pelo número ${numeroCurto(n)}` : `Número ${numeroCurto(n)} desligado: a resposta não sai`}
      /* ENCOLHE: no telefone a linha de apoio tem pouco depois do número do
         cliente, e a pílula passava por baixo dos botões do topo. Quem diz
         "desligado" em qualquer largura é o aviso acima do composer. */
      className={`flex min-w-0 shrink items-center gap-1 overflow-hidden rounded-etiqueta px-1.5 font-mono text-apoio leading-[20px] whitespace-nowrap ${
        pareado ? "bg-foreground/6 text-tinta-suave" : "bg-veu-espera font-semibold text-terra"
      }`}
    >
      <span className="flex flex-none">
        <Icone de={ICONES.whatsapp} tamanho={12} />
      </span>
      <span className="flex-none">{numeroCurto(n)}</span>
    </span>
  );
}

export default function CabecalhoDaConversa({
  caso,
  empurraPainel,
  painelNaTela,
  listaNaTela,
  aoAlternarLista,
  aoAlternarPainel,
}) {
  const { caso: dados, presenca, numerosDoSetor } = useEstado();
  const larguraPrincipal = useLarguraPrincipal(empurraPainel, listaNaTela);
  const { arquivarCaso, desarquivarCaso, apagarCaso, salvarContato } = useAcoes();
  const pode = usePode();
  const nav = useNavegacao();
  const celular = useCelular();
  /*
   * O ← e o nível do título perguntam pela LISTA, não pela largura.
   *
   * Perguntavam `useCelular()` (760px), e a lista sai da tela em duas faixas
   * diferentes: abaixo de 760 (o celular) e entre 880 e 1179, onde a ficha
   * empurra e a coluna de conversas é sacrificada. Nessa segunda faixa — um
   * notebook de 1366 com a janela pela metade, um iPad na horizontal — não havia
   * ← nenhum: a conversa abria e a única saída era o trilho ou o botão do
   * navegador. E o `<h1>` da página é o da lista: sem ela montada, a tela ficava
   * sem primeiro nível para o leitor de tela.
   */
  const semLista = !listaNaTela;
  const Nome = semLista ? "h1" : "span";
  const cor = tinta(caso.tese ?? caso.etapa);
  const delegado = Boolean(dados?.delegacao);
  const dito = presencaEmPalavras(presenca);
  /* O que espera decisão na ficha: passo do servidor, item da fila de automação
     e pendência apertada. É a conta do ponto no botão do painel. */
  const esperando =
    (dados?.pauta?.passos?.length ?? 0) +
    (dados?.pauta?.fila?.length ?? 0) +
    (dados?.pendencias ?? []).filter((p) => p.atraso > 0).length;

  const defs = [
    /*
     * A PETIÇÃO saiu daqui (27/08) e virou widget da coluna do caso
     * (`WidgetDaPeticao.jsx`).
     *
     * Ela era a única ação sólida do topo, e estava no lugar errado por dois
     * motivos. Espaço: esta faixa é a mais disputada da mesa — nome, telefone,
     * tese, presença e três ações —, e a petição empurrava o nome do cliente
     * para as reticências no notebook com o painel aberto. Assunto: petição é
     * ESTADO DO CASO, como documento e prazo, e o lugar disso é a coluna dele.
     *
     * E como botão ela dizia menos: um ícone que abria uma tela. O widget diz se
     * a peça existe, em que ponto está e o que ainda fura o texto — sem abrir.
     */
    /* Com a IA no comando, quem tem o "Ajustar" é a BARRA DE ESCREVER (a faixa
       verde dentro dela) — mesmo destino, e no lugar onde a pessoa decide falar.
       Dois botões para a mesma porta é o que fazia este topo transbordar. */
    ...(delegado
      ? []
      : [
          {
            chave: "ia",
            rotulo: "IA",
            dica: "Delegar este atendimento ao agente, com escopo",
            icone: ICONES.ia,
            fg: "var(--color-verde-fundo)",
            aoTocar: () => nav.ajustar({ delegar: true }),
          },
        ]),
    {
      chave: "busca",
      rotulo: "Procurar",
      dica: "Procurar dentro desta conversa",
      icone: ICONES.busca,
      /* Interruptor: o botão diz se a busca está ligada. */
      ligado: Boolean(nav.procurarAberto || nav.naConversa),
      fg: "var(--color-tinta)",
      /* Procurar dentro da conversa: a busca da mesa acha o atendimento, esta
         acha a mensagem — o número do processo que o cliente mandou em abril. */
      aoTocar: () =>
        nav.ajustar(nav.procurarAberto || nav.naConversa ? { procurar: null, naConversa: null } : { procurar: true }),
    },
  ];

  /*
   * Editar, arquivar e apagar o atendimento — daqui, e não de outra tela.
   *
   * Estas três NUNCA vão para a barra, por larga que a tela seja: apagar ao lado
   * de "Painel" é o clique errado esperando acontecer. Ficam sempre no menu, que
   * abre com intenção e pergunta antes. "Editar" não abre formulário novo — leva
   * à ficha, que é onde nome, telefone e campos já se editam; o que faltava era
   * o caminho até ela.
   */
  /* Arquivar vem PRIMEIRO: é o fechar do LiderHub, gesto de toda conversa
     resolvida — atrás de "Editar ficha" e do que transbordou da barra, a
     atendente procurava (Laura, 14/10). */
  const doMenu = [
    {
      chave: "editar",
      rotulo: "Editar ficha",
      icone: ICONES.editar,
      bg: "var(--color-papel)",
      bd: "color-mix(in oklab, var(--color-foreground) 13%, transparent)",
      fg: "var(--color-tinta)",
      aoTocar: () => nav.ajustar({ painel: "case" }),
    },
    ...(pode("registros:arquivar")
      ? [
          /* Arquivado oferece a volta, não arquivar de novo: o menu dizia
             "Arquivar" num caso já arquivado e o gesto parecia não pegar. */
          dados?.registro?.arquivado
            ? {
                chave: "desarquivar",
                rotulo: "Trazer de volta para a mesa",
                icone: ICONES.voltar,
                bg: "var(--color-papel)",
                bd: "color-mix(in oklab, var(--color-foreground) 13%, transparent)",
                fg: "var(--color-tinta)",
                aoTocar: () => desarquivarCaso(),
              }
            : {
                chave: "arquivar",
                rotulo: "Arquivar atendimento",
                icone: ICONES.arquivar,
                bg: "var(--color-papel)",
                bd: "color-mix(in oklab, var(--color-foreground) 13%, transparent)",
                fg: "var(--color-tinta)",
                aoTocar: arquivarCaso,
              },
        ]
      : []),
    ...(pode("registros:apagar")
      ? [
          {
            chave: "apagar",
            rotulo: "Apagar de vez",
            icone: ICONES.apagar,
            bg: "var(--color-papel)",
            bd: "color-mix(in oklab, var(--color-terra) 35%, transparent)",
            fg: "var(--color-terra)",
            aoTocar: apagarCaso,
          },
        ]
      : []),
  ];

  const { cabem, comRotulo, sobra } = acoesQueCabem(defs, larguraPrincipal, celular);

  /*
   * O atalho "avançar para X" SAIU da linha da identidade (27/08).
   *
   * Ele nasceu porque a etapa estava aqui como texto morto e mover custava abrir
   * o painel. Agora o trilho logo abaixo MOVE (`TrilhoDoProcesso.jsx`), com o
   * caminho inteiro e um alvo de coluna — e os dois ficaram a 8px de distância
   * fazendo a mesma coisa, um deles numa pastilha de 24px encostada no telefone
   * do cliente. Duas portas para o mesmo assunto na mesma faixa é o defeito que
   * a mesa já apagou duas vezes (as abas do painel, a caixa do WhatsApp).
   *
   * O que se perdeu: um toque para o passo SEGUINTE sem escolher entre nove. O
   * que se ganhou: a linha da identidade de volta para nome e telefone, e um
   * lugar só onde a etapa se move. Se o passo seguinte num toque fizer falta, ele
   * volta como o primeiro item do menu "mais ações" — não como pastilha aqui.
   */

  return (
    /* Duas faixas, e não uma: em cima quem é o cliente e o que dá para fazer;
       embaixo onde o caso está no processo. Estavam juntas — a etapa era uma
       palavra solta no meio do telefone e do responsável —, e ninguém lia o
       funil de uma conversa aberta sem abrir o painel. */
    <div className="flex flex-none flex-col border-b border-b-foreground/9 bg-muted">
      {/*
       * A FAIXA DA IDENTIDADE, em duas linhas de altura fixa.
       *
       * O que mudou, e por quê:
       *
       *   - **o campo de edição não é mais cortado.** O nome e o telefone viram
       *     `<input>` ao serem tocados, e eles moravam dentro de caixas com
       *     `overflow-hidden` + `truncate` — o recorte é do modo LEITURA (nome
       *     longo em coluna estreita), e ficava valendo para o campo, que nascia
       *     com meia letra e a borda comida. Agora quem trunca é o próprio texto
       *     em leitura, e o container só limita a largura (`min-w-0`).
       *   - **hierarquia**: o nome é o único elemento grande; a tese vira selo
       *     discreto ao lado; telefone, presença e etiquetas são a linha de
       *     apoio, todos no mesmo tom e tamanho.
       *   - **compacto**: 8px de respiro vertical em vez de 10, retrato de 36 e
       *     as duas linhas com altura própria — a faixa deixa de "pular" quando
       *     a presença aparece ou some.
       */}
      <div className="relative flex items-center gap-2.5 px-3.5 py-2">
        {/*
         * O botão de SAIR do atendimento existe sempre, e é isso que mudou.
         *
         * Ele só nascia sem a lista na tela — ali era a única saída da conversa.
         * Com a lista montada não havia como DESSELECIONAR: aberta uma conversa,
         * a mesa ficava presa numa até alguém clicar em outra, e `/atendimentos`
         * pelado (que agora é o painel, o kanban e a tabela do recorte) só se
         * alcançava pelo trilho ou apagando a URL na mão.
         *
         * O ícone diz qual das duas coisas é: seta quando VOLTA para a lista (a
         * lista não está na tela), ✕ quando FECHA o caso e a lista continua ao
         * lado. Mesmo destino, gestos diferentes — e ✕ com a lista visível
         * prometeria fechar a coluna.
         */}
        <Button
          variant="outline"
          onClick={nav.voltarParaLista}
          aria-label={semLista ? "Voltar para a lista de atendimentos" : "Fechar este atendimento"}
          data-dica={semLista ? "Voltar para a lista" : "Fechar o atendimento e ver o painel da mesa"}
          className="alvo size-padrao flex-none rounded-controle border-foreground/12 bg-card p-0 text-foreground hover:text-foreground"
        >
          <Icone de={semLista ? ICONES.voltar : ICONES.fechar} tamanho={16} />
        </Button>

        <InterruptorDeColuna lado="esquerda" aberta={listaNaTela} aoAlternar={aoAlternarLista} />

        <Retrato nome={caso.nome} registro={caso.id} tamanho={36} />

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex min-w-0 items-center gap-2">
            {/*
             * O nome EDITA aqui, e não no painel: quem corrige "CLEIDE APARECIDA
             * BONFIN" está olhando ESTE nome, que é o grande. `flex-1` com base
             * ZERO porque o botão do `TextoEditavel` tem `max-w-full`, e porcentagem
             * dentro de item flex de base `auto` não tem a que se referir — era
             * assim que o nome saía como "Cre…" com meia faixa vazia à direita.
             *
             * Sem a lista montada é `h1` (ela é que tem o dela); com a lista na
             * tela é `span`, senão a tela fica com dois primeiros níveis.
             */}
            {/* O token título em qualquer largura: eram 16, 17 e 19 escolhidos
                pela sobra, e o nome mudava de corpo a cada coluna que abria. Quem
                não cabe vira reticências, que é o que o `truncate` já faz. */}
            <Nome className="m-0 min-w-0 flex-1 font-serif text-forte leading-[1.2] tablet:text-titulo">
              <TextoEditavel
                valor={caso.nome ?? ""}
                rotulo="Nome do cliente"
                aoGravar={(nome) => nome && salvarContato({ nome })}
              />
            </Nome>
            {/* A TESE é do ESQUEMA (decide o checklist e a peça) e a dica diz
                isso: em caixa alta e colorida, ela parecia uma etiqueta — e
                etiqueta agora existe de verdade, na linha de baixo. `flex-none`
                para não ser ela a virar reticências. */}
            {larguraPrincipal >= 560 && (
              <span className="flex-none" data-dica="Tese do caso — decide os documentos exigidos e a peça. Muda na ficha.">
                <Etiqueta cores={cor}>{caso.tese ?? "sem tese"}</Etiqueta>
              </span>
            )}
          </div>

          {/* A LINHA DE APOIO: presença, telefone e etiquetas, no mesmo tom.
              `min-h` fixo para a faixa não pular quando "digitando…" aparece. */}
          {larguraPrincipal >= 380 && (
            <div className="flex min-h-[22px] min-w-0 items-center gap-2 text-apoio text-tinta-suave">
              {/* "digitando…" vale mais que o telefone naquele segundo: é o que
                  decide se quem atende espera a resposta ou vai para o próximo
                  caso. Só o WhatsApp Web entrega isso. */}
              {dito ? (
                <span className={`flex-none ${dito.vivo ? "font-bold text-verde" : "font-normal text-muted-foreground"}`}>
                  {dito.texto}
                </span>
              ) : null}
              {/* Telefone como se lê em voz alta — "48996324532" cru é número de
                  banco de dados, e este é o mesmo que alguém disca. Edita aqui,
                  pelo motivo do nome. Largura própria (não `overflow-hidden`)
                  para o campo caber inteiro quando abrir. */}
              {/* Bandeira e número são UMA coisa — o estado é do número —, e
                  ficam colados (6px: a borda do campo aberto encosta na bandeira, sem cobri-la); o vão da linha (8px) separa de "digitando…"
                  e da sigla do número do setor. */}
              <span className="flex flex-none items-center gap-1.5">
                <Bandeira telefone={caso.telefone} altura={11} />
                <span className="flex min-w-[128px] flex-none">
                  <TextoEditavel
                    valor={caso.telefone ?? ""}
                    exibido={telefoneLegivel(caso.telefone) || caso.telefone || "sem telefone"}
                    rotulo="Telefone do cliente"
                    convite="27 99999-9999"
                    classe="font-mono text-apoio"
                    mascara={mascaraDeTelefone}
                    /* Grava só os dígitos: a máscara é desenho, e o envio e a
                       comparação de contato leem dígito. */
                    aoGravar={(telefone) => salvarContato({ telefone: telefone.replace(/\D/g, "") || null })}
                  />
                </span>
              </span>
              {/* No telefone a linha não tem largura para a sigla depois do
                  número do cliente (sobrava uma lasca de 4px); lá quem fala do
                  número é o aviso acima do composer, quando ele está desligado. */}
              {celular ? null : <NumeroDoCaso numeros={numerosDoSetor} conexaoId={dados?.conexao_id} />}
              {/* As ETIQUETAS saíram daqui em 09/09.
                  Elas moravam nesta linha de apoio, atrás de "digitando…" e do
                  telefone, e só apareciam acima de 380px de conversa — com a
                  ficha aberta num notebook, quase nunca. Pior: o CATÁLOGO do
                  escritório (renomear e apagar o rótulo de todos os casos) só
                  existia dentro de um combobox de 210px que fecha ao perder o
                  foco. Hoje são widget da coluna do caso, com o gerenciador
                  inteiro em folha — e a linha da lista continua mostrando as
                  duas primeiras, que é o glance de quem varre a mesa. */}
            </div>
          )}
        </div>

        {/* As ações moram juntas, com um fio à esquerda: sem a separação elas
            pareciam a continuação da linha do cliente, e o olho não sabia onde
            acabava a identidade e começava o que dá para fazer. */}
        <div className="flex flex-none items-center gap-1 self-center border-l border-l-foreground/9 pl-2.5">
          {cabem.map((acao, i) => (
            <BotaoDeAcao key={acao.chave} acao={acao} comRotulo={i < comRotulo} />
          ))}
          {/* FORA do `cabem`: interruptor de coluna não disputa espaço com ação
              do caso. Na ponta direita porque é a coluna que ele abre — a
              posição é metade do que faz o botão ser entendido sem rótulo. */}
          <InterruptorDeColuna lado="direita" aberta={painelNaTela} aoAlternar={aoAlternarPainel} esperando={esperando} />

          {sobra.concat(doMenu).length > 0 && (
            <MenuDeAcoes
              rotulo="Mais ações"
              titulo="Mais ações deste atendimento"
              largura="230px"
              ancora="bottom end"
              itens={[
                ...doMenu.filter((a) => a.chave === "arquivar"),
                ...sobra,
                ...doMenu.filter((a) => a.chave !== "arquivar"),
              ].map((a) => ({ ...a, cor: a.fg }))}
              /* Classe e não estilo: o `style` inline não tem hover nem foco, e o
                 ⋯ era o único botão do topo que não reagia ao ponteiro. Aberto
                 fica escuro como os interruptores ligados. */
              classe="flex size-padrao flex-none cursor-pointer items-center justify-center rounded-controle border-0 bg-transparent text-tinta-media transition-[background-color,color] duration-(--t-curta) ease-entra hover:bg-card hover:text-foreground data-popup-open:bg-escuro data-popup-open:text-sobre-escuro"
            >
              <Icone de={ICONES.mais} tamanho={16} />
            </MenuDeAcoes>
          )}
        </div>
      </div>

      {/* O caminho inteiro, sempre: em tela estreita ele rola na horizontal em
          vez de virar outra coisa. */}
      <TrilhoDoProcesso />
    </div>
  );
}
